import type React from 'react'
import { displayTable } from '../../utils/tableLabel'
import { formatClock, formatDayKey } from '../../utils/dayKey'
import { formatGr, totalNetGr } from '../../utils/lineMath'
import { formatTRY } from '../../utils/money'
import { printedSlips, splitByStation, type StationSlip } from '../../utils/stations'
import {
  DEFAULT_CASH_RECEIPT,
  DEFAULT_RECEIPT,
  RECEIPT_BRAND,
  type Order,
  type OrderLine,
  type ReceiptSettings,
} from '../../types'
import './receipt.css'

/**
 * Who rang up the lines on this slip.
 *
 * Normally one person: a slip is one round, and a round is one person's. A
 * reprint can gather several, and then every name is printed rather than none
 *, the stall is holding food from both of them, and knowing which two people
 * to go back to beats knowing neither.
 */
function slipWaiter(lines: OrderLine[]): string | undefined {
  const names = [...new Set(lines.map((l) => l.byName).filter(Boolean))]
  return names.length > 0 ? names.join(', ') : undefined
}

/**
 * Which of the two slips this is. They answer different questions, one says
 * what to prepare, the other what was charged, so each has its own settings.
 */
export type ReceiptKind = 'station' | 'cash' | 'move'

interface Props {
  order: Order | null
  tenantName: string
  /** What to print; omitted fields fall back to how the slip always looked. */
  settings?: ReceiptSettings
  kind?: ReceiptKind
}

/**
 * The slip carries no prices at all: it is handed to a stall as a preparation
 * order, and an amount on it invites being asked for money that was already
 * taken at the till.
 *
 * Quantity moves to the front of the line, where the price used to sit on the
 * right. Someone working a counter reads down the left edge.
 */
type Resolved = ReturnType<typeof resolve>

/**
 * Fills in whatever the yurt has not set. Undefined means "never configured",
 * so it takes the default; an empty string is a real choice and prints nothing.
 */
export function resolve(
  settings?: ReceiptSettings,
  defaults: Required<Omit<ReceiptSettings, 'title' | 'skipStations' | 'subtitle'>> = DEFAULT_RECEIPT,
) {
  return {
    title: settings?.title ?? '',
    footer: settings?.footer ?? defaults.footer,
    showReceiptNo: settings?.showReceiptNo ?? defaults.showReceiptNo,
    showDate: settings?.showDate ?? defaults.showDate,
    showTable: settings?.showTable ?? defaults.showTable,
    showWaiter: settings?.showWaiter ?? defaults.showWaiter,
    linePercent: settings?.linePercent ?? defaults.linePercent,
    fontPercent: settings?.fontPercent ?? defaults.fontPercent,
    feedMm: settings?.feedMm ?? defaults.feedMm,
    ruleWidthPx: settings?.ruleWidthPx ?? defaults.ruleWidthPx,
    ruleStyle: settings?.ruleStyle ?? defaults.ruleStyle,
    boxStation: settings?.boxStation ?? defaults.boxStation,
    showStationTotal: settings?.showStationTotal ?? defaults.showStationTotal,
    showFooterStation: settings?.showFooterStation ?? defaults.showFooterStation,
    showPrices: settings?.showPrices ?? defaults.showPrices,
    splitByStation: settings?.splitByStation ?? defaults.splitByStation,
  }
}

function Lines({ lines, showPrices }: { lines: OrderLine[]; showPrices: boolean }) {
  return (
    <>
      {lines.map((line, i) => (
        <div key={`${line.productId}-${line.note ?? ''}-${i}`} className="r-line">
          <div className="row">
            <span className="r-qty">{line.qty}</span>
            <span className="name">{line.name}</span>
            {showPrices && <span className="amt">{formatTRY(line.lineTotalKurus)}</span>}
          </div>

          {/* The note is what the stall acts on, so it prints in bold under
              the item rather than being tucked in beside the name. */}
          {line.note && (
            <div className="row r-line__note">
              <span className="name">» {line.note}</span>
            </div>
          )}

          {/* Tartılı satır: müşteri fişinde tabak tabak net gram ve tutar,
              altında toplam kilo ve kap. Mutfak fişinde (fiyatsız) yalnız adet. */}
          {showPrices && line.weigh?.status === 'done' && line.weigh.plates && (
            <>
              {line.weigh.plates.map((p, k) => (
                <div key={k} className="row r-line__weigh">
                  <span className="name">• Porsiyon {k + 1}: {p.netGr} g</span>
                  <span className="amt">{formatTRY(p.kurus)}</span>
                </div>
              ))}
              <div className="row r-line__weigh">
                <span className="name">
                  Toplam: {formatGr(totalNetGr(line.weigh.plates))}
                  {line.weigh.tare ? ` (${line.weigh.tare.name})` : ''}, {formatTRY(line.weigh.perKgKurus)}/kg
                </span>
              </div>
            </>
          )}
          {showPrices && line.weigh?.status === 'pending' && (
            <div className="row r-line__weigh">
              <span className="name">• Tartılacak</span>
            </div>
          )}
        </div>
      ))}
    </>
  )
}

function Header({
  order,
  tenantName,
  slip,
  index,
  count,
  cfg,
}: {
  order: Order
  tenantName: string
  slip: StationSlip
  index: number
  count: number
  cfg: Resolved
}) {
  return (
    <>
      {order.reprint && <div className="r-reprint">TEKRAR BASIM</div>}
      <div className="r-head">
        <div className="r-title">{cfg.title || tenantName}</div>
        {/* The station is the largest thing on the slip: whoever is handed it
            needs to know at a glance whether it is theirs. */}
        {count > 1 && <div className="r-station">{slip.station}</div>}
        {/* Her fişte, işletme değiştiremez (28 Eki 2026, kullanıcı). */}
        <div className="r-sub">{RECEIPT_BRAND}</div>
        {count > 1 && <div className="r-sub">{`${index + 1} / ${count}`}</div>}
      </div>

      {/* Gel-Al numarası (21 Eki 2026): müşteriye "3 numara hazır" diye
          seslenilsin, büyük ve başta. Yalnız masasız satışta. */}
      {!order.tableName && order.takeawayNo && (
        <div className="r-takeaway">GEL-AL #{order.takeawayNo}</div>
      )}

      <hr />

      {/* An open ticket has no receipt number yet, it's issued at payment,
          so an abandoned ticket never burns one. The table identifies the
          slip until then. */}
      {cfg.showReceiptNo && order.receiptNo && (
        <div className="row">
          <span className="name">Fiş No</span>
          <span className="amt">{order.receiptNo}</span>
        </div>
      )}
      {cfg.showTable && order.tableName && (
        <div className="row">
          <span className="name">Masa</span>
          <span className="amt">{displayTable(order.tableName)}</span>
        </div>
      )}
      {/* Who rang up the lines on this slip, not who opened the table.
          A table is often opened by one waiter and added to by another, and
          the stall's question is who to hand the food back to, naming the
          opener sent them to someone who never took the order.

          Read from the lines, falling back to the opener for tickets rung up
          before lines carried a name. */}
      {cfg.showWaiter && (slipWaiter(slip.lines) ?? order.openedByName) && (
        <div className="row">
          <span className="name">Garson</span>
          <span className="amt">{slipWaiter(slip.lines) ?? order.openedByName}</span>
        </div>
      )}
      {cfg.showDate && (
        <div className="row">
          <span className="name">Tarih</span>
          <span className="amt">
            {formatDayKey(order.dayKey)} {formatClock(order.createdAtLocal)}
          </span>
        </div>
      )}
    </>
  )
}

/**
 * Rendered permanently into the layout but visible only to the print
 * stylesheet, see receipt.css. Not a route.
 *
 * One slip per station: a customer holding a single slip can only hand it to
 * one stall, so the grill would never see what the drinks counter owes them.
 *
 * No amounts anywhere. Payment happens at the till before any of this is
 * printed, so the slip's only job is to say what to hand over.
 */
export function Receipt({ order, tenantName, settings, kind = 'station' }: Props) {
  if (!order) return <div id="receipt-root" aria-hidden="true" />
  if (kind === 'move' && order.move) return <MoveReceipt order={order} settings={settings} />

  const cfg = resolve(settings, kind === 'cash' ? DEFAULT_CASH_RECEIPT : DEFAULT_RECEIPT)
  // The till's copy stays whole. Splitting by station exists so each stall gets
  // the part it has to prepare; the till is keeping a record of one sale, and
  // cutting it into four would file the same sale four times.
  const slips: StationSlip[] =
    kind === 'cash' && !cfg.splitByStation
      ? [
          {
            station: tenantName,
            lines: order.lines,
            totalKurus: order.totalKurus,
            itemCount: order.itemCount,
          },
        ]
      : kind === 'cash'
        ? splitByStation(order.lines)
        : printedSlips(splitByStation(order.lines), settings)

  return (
    <div
      id="receipt-root"
      aria-hidden="true"
      style={
        {
          // Scaled rather than replaced: receipt.css still decides the sizes,
          // these just stretch or squeeze them as one.
          '--r-line': cfg.linePercent / 100,
          '--r-font': cfg.fontPercent / 100,
          '--r-feed': `${cfg.feedMm}mm`,
          '--r-rule-w': `${cfg.ruleWidthPx}px`,
          '--r-rule-s': cfg.ruleStyle,
        } as React.CSSProperties
      }
    >
      {slips.map((slip, i) => {
        return (
          <div key={slip.station} className="r-slip" data-station={slip.station}>
            <Header
              order={order}
              tenantName={tenantName}
              slip={slip}
              index={i}
              count={slips.length}
              cfg={cfg}
            />

            <hr />

            <Lines lines={slip.lines} showPrices={cfg.showPrices} />

            <hr />

            {/* The station name already sits at the top in the largest type on
                the slip, so on a single-station restaurant this line only repeats
                it. Kept for anyone who wants the count, off by choice. */}
            {cfg.showStationTotal && (
              <div className="row payline">
                <span className="name">{slip.station}</span>
                <span className="amt">{slip.itemCount} ürün</span>
              </div>
            )}

            {/* Money belongs on the till's own slip, not on one handed to a
                stall. Discounts are shown as the amount taken off, because that
                is the figure anyone checking the slip would reach for. */}
            {cfg.showPrices && (
              <>
                {order.discountKurus || order.campaigns?.length || order.prepaidKurus ? (
                  <>
                    <div className="row">
                      <span className="name">Ara Toplam</span>
                      <span className="amt">
                        {formatTRY(order.subtotalKurus ?? order.totalKurus)}
                      </span>
                    </div>
                    {order.campaigns?.map((c) => (
                      <div className="row" key={c.id}>
                        <span className="name">Kampanya: {c.name}</span>
                        <span className="amt">−{formatTRY(c.kurus)}</span>
                      </div>
                    ))}
                    {/* Kapanıştan önce alınan ara ödemeler (22 Eki 2026). */}
                    {order.prepaidKurus ? (
                      <div className="row">
                        <span className="name">Ara ödemeler</span>
                        <span className="amt">−{formatTRY(order.prepaidKurus)}</span>
                      </div>
                    ) : null}
                    {order.discountKurus ? (
                      <div className="row">
                        <span className="name">İndirim %{order.discountPercent ?? ''}</span>
                        <span className="amt">−{formatTRY(order.discountKurus)}</span>
                      </div>
                    ) : null}
                  </>
                ) : null}
                <div className="row payline">
                  {/* Ürünsüz ara ödeme fişi: "TOPLAM" yerine ne olduğu. */}
                  <span className="name">{order.partialOf ? 'ARA ÖDEME' : 'TOPLAM'}</span>
                  <span className="amt">{formatTRY(order.totalKurus)}</span>
                </div>
                {/* Under the total, not folded into it: the customer who left
                    it is owed a line saying so, and the figure above has to
                    stay the one the menu adds up to. */}
                {order.bahsisKurus ? (
                  <>
                    <div className="row">
                      <span className="name">Bahşiş</span>
                      <span className="amt">{formatTRY(order.bahsisKurus)}</span>
                    </div>
                    <div className="row payline">
                      <span className="name">ÖDENEN</span>
                      <span className="amt">
                        {formatTRY(order.totalKurus + order.bahsisKurus)}
                      </span>
                    </div>
                  </>
                ) : null}
              </>
            )}

            {order.note && (
              <div className="row">
                <span className="name">Not</span>
                <span className="amt">{order.note}</span>
              </div>
            )}

            {/* Tezgah satırının altında çizgi yok: üstteki çizgiyle birlikte
                tezgahın etrafında çerçeve gibi duruyordu (kullanıcı, 13 Eki
                2026). Tutar ya da not varsa ayraç kalıyor. */}
            {(cfg.showPrices || order.note) && <hr />}

            {/* No "ödeme alınmadı" stamp any more: the slip carries no amounts
                at all, so there is nothing on it anyone could mistake for a
                bill. The station is what the person holding it needs. */}
            <div className="r-foot">
              {cfg.showFooterStation && (
                <div className="r-thanks">
                  {slip.station}
                </div>
              )}
              {cfg.footer && <div className="r-note">{cfg.footer}</div>}
            </div>

            <div className="feed" />
          </div>
        )
      })}
    </div>
  )
}

/**
 * "MASA DEĞİŞTİ" fişi (23 Eki 2026): tezgahta bekleyen ürünlerin masası
 * değişti. Sipariş fişine benzemiyor (başlık, ters zeminli bant, yön oku),
 * ürünler ikinci kez yapılmasın diye altta açıkça yazıyor.
 */
function MoveReceipt({ order, settings }: { order: Order; settings?: ReceiptSettings }) {
  const cfg = resolve(settings, DEFAULT_RECEIPT)
  const move = order.move!
  const slips = printedSlips(splitByStation(order.lines), settings)
  return (
    <div
      id="receipt-root"
      aria-hidden="true"
      style={
        {
          '--r-line': cfg.linePercent / 100,
          '--r-font': cfg.fontPercent / 100,
          '--r-feed': `${cfg.feedMm}mm`,
          '--r-rule-w': `${cfg.ruleWidthPx}px`,
          '--r-rule-s': cfg.ruleStyle,
        } as React.CSSProperties
      }
    >
      {slips.map((slip) => (
        <div key={slip.station} className="r-slip r-move" data-station={slip.station}>
          {order.reprint && <div className="r-reprint">TEKRAR BASIM</div>}
          <div className="r-move__band">MASA DEĞİŞTİ</div>
          <div className="r-sub">{RECEIPT_BRAND}</div>
          <div className="r-move__route">
            <span className="r-move__from">{displayTable(move.from)}</span>
            <span className="r-move__arrow">→</span>
            <span className="r-move__to">{displayTable(order.tableName) || 'Gel-Al'}</span>
          </div>
          <div className="r-station r-move__station">{slip.station}</div>

          <hr />
          <Lines lines={slip.lines} showPrices={false} />
          <hr />

          <div className="row">
            <span className="name">Saat</span>
            <span className="amt">{formatClock(move.at)}</span>
          </div>
          {move.by && (
            <div className="row">
              <span className="name">Taşıyan</span>
              <span className="amt">{move.by}</span>
            </div>
          )}

          <div className="r-move__warn">Yeni sipariş değil, tekrar hazırlamayın.</div>
          <div className="feed" />
        </div>
      ))}
    </div>
  )
}
